/**
 * PURPOSE: Reglas y utilidades puras de la administración SISDEU (FTR-0017 Phase 4): vínculos de
 *   institución, política de capacidades de compañía y bandeja de alertas.
 * RESPONSIBILITIES: Mensajes de error por estado/código, clasificación de errores (deshabilitado por
 *   compañía vs indisponible vs sin permiso vs conflicto de validación), etiquetas de nivel de confianza
 *   y de alerta, explicación segura de una alerta (nunca usuario ni dato externo sensible, solo ids y
 *   conteos ya expuestos por el backend), formato del resumen de sincronización, deduplicado defensivo
 *   de alertas y el impacto de un cambio de política calculado a partir de datos ya cargados (sin
 *   endpoint de vista previa en el backend). Sin red ni axios: se prueba con Jest.
 * COLLABORATORS: sisdeuAdminService.js, SisdeuInstitutionLinksModal.jsx, SisdeuPolicyModal.jsx,
 *   SisdeuAlertsModal.jsx, portalAccounts.utils.js (catálogo de capacidades reutilizado).
 * ROUTE: winscore/src/admin/AdminCompany/sisdeuAdmin.utils.js
 */
export const CONFIDENCE_TIER_LABELS = Object.freeze({
  high: "Alta",
  medium: "Media",
  unmatched: "Sin coincidencia",
});

export const getConfidenceTierLabel = (tier) => CONFIDENCE_TIER_LABELS[tier] || tier || "";

const CODE_MESSAGES = Object.freeze({
  DELEGATE_PORTAL_DISABLED: "El portal de delegados no está habilitado.",
  DELEGATE_PORTAL_COMPANY_DISABLED: "El portal de delegados no está habilitado para esta compañía.",
  SISDEU_LOGIN_COMPANY_DISABLED: "El acceso con cuentas SISDEU no está habilitado para esta compañía.",
  SISDEU_UNAVAILABLE: "SISDEU no está disponible en este momento. Intenta nuevamente en unos minutos.",
  SESSION_REQUIRED: "Tu sesión expiró. Vuelve a iniciar sesión.",
  USER_NOT_ENABLED: "Tu usuario no está habilitado.",
  COMPANY_ADMIN_ROLE_REQUIRED: "Tu rol no puede administrar accesos de delegados.",
  COMPANY_NOT_ADMINISTRABLE: "No puedes administrar esta compañía.",
  COMPANY_CONTEXT_MISMATCH: "La compañía indicada no coincide con la que administras.",
  INVALID_CAPABILITY: "Una de las capacidades enviadas no es válida.",
  INVALID_ALERT_STATUS: "El filtro de alertas no es válido.",
  ALERT_ALREADY_RESOLVED: "Esa alerta ya fue resuelta.",
  INSTITUTION_ALREADY_LINKED: "Esa institución ya tiene un vínculo SISDEU aprobado.",
  SISDEU_INSTITUTION_ALREADY_LINKED: "Esa institución SISDEU ya está vinculada a otra institución.",
  SISDEU_INSTITUTION_NOT_FOUND: "La institución SISDEU indicada no existe.",
  SISDEU_INSTITUTION_INACTIVE: "La institución SISDEU indicada está inactiva.",
});

/** Módulo apagado para esta compañía (distinto de "no autorizado" y de "SISDEU no responde"). */
export const isSisdeuCompanyDisabledError = (error) =>
  error?.response?.status === 403 && error?.response?.data?.code === "SISDEU_LOGIN_COMPANY_DISABLED";

/** Indisponibilidad temporal del gateway SISDEU (503): reintentable, no es un problema de permisos. */
export const isSisdeuUnavailableError = (error) =>
  error?.response?.status === 503 && error?.response?.data?.code === "SISDEU_UNAVAILABLE";

/** Portal de delegados apagado del todo o para esta compañía (FTR-0013/0012, no específico de SISDEU). */
export const isDelegatePortalUnavailableError = (error) =>
  ["DELEGATE_PORTAL_DISABLED", "DELEGATE_PORTAL_COMPANY_DISABLED"].includes(error?.response?.data?.code);

/** Insuficiente autorización (FTR-0012): rol o compañía, nunca confundido con un módulo apagado. */
export const isCompanyAdminForbiddenError = (error) =>
  error?.response?.status === 403 &&
  ["COMPANY_ADMIN_ROLE_REQUIRED", "COMPANY_NOT_ADMINISTRABLE"].includes(error?.response?.data?.code);

/** Mensaje para el administrador según estado HTTP y código estable del backend. */
export const getSisdeuAdminErrorMessage = (error, fallback) => {
  if (!error?.response) return "No se pudo conectar con el servidor.";
  const { status } = error.response;
  const data = error.response.data || {};
  if (data.code && CODE_MESSAGES[data.code]) return CODE_MESSAGES[data.code];
  if (status === 401) return CODE_MESSAGES.SESSION_REQUIRED;
  if (status === 429) return "Demasiadas solicitudes. Intenta nuevamente en unos segundos.";
  const serverMessage = data.message || data.msg;
  if ([400, 403, 404, 409].includes(status) && serverMessage) return serverMessage;
  if (status === 403) return "No tienes permiso para realizar esta acción.";
  if (status === 404) return "No se encontró el recurso solicitado.";
  return fallback;
};

export const ALERT_TYPE_LABELS = Object.freeze({
  SISDEU_INSTITUTION_CHANGED: "Cambio de institución en SISDEU",
  SISDEU_DUPLICATE_CONFLICT: "Conflicto de cuentas SISDEU duplicadas",
});

export const getAlertTypeLabel = (type) => ALERT_TYPE_LABELS[type] || type || "Alerta";

/**
 * Explicación segura de una alerta: solo ids y conteos que el backend ya expone (D12/D7). Nunca arma un
 * texto con usuario, documento o dato de contacto porque `detail` nunca los contiene.
 */
export const describeAlert = (alert) => {
  const detail = alert?.detail || {};
  if (alert?.type === "SISDEU_INSTITUTION_CHANGED") {
    return {
      title: getAlertTypeLabel(alert.type),
      lines: [
        "El vínculo anterior quedó bloqueado y sus permisos no se transfirieron a la nueva institución.",
        detail.previousExternalInstitutionId != null && detail.currentExternalInstitutionId != null
          ? `Institución externa SISDEU: ${detail.previousExternalInstitutionId} → ${detail.currentExternalInstitutionId}.`
          : null,
      ].filter(Boolean),
    };
  }
  if (alert?.type === "SISDEU_DUPLICATE_CONFLICT") {
    const candidateCount = Array.isArray(detail.candidates) ? detail.candidates.length : 0;
    return {
      title: getAlertTypeLabel(alert.type),
      lines: [
        candidateCount
          ? `${candidateCount} cuentas SISDEU activas coinciden en usuario y contraseña; el acceso quedó denegado hasta revisión.`
          : "Varias cuentas SISDEU activas coinciden en usuario y contraseña; el acceso quedó denegado hasta revisión.",
        Array.isArray(detail.differences) && detail.differences.length
          ? `Difieren en: ${detail.differences.join(", ")}.`
          : null,
      ].filter(Boolean),
    };
  }
  return { title: alert?.summary || getAlertTypeLabel(alert?.type), lines: [] };
};

/**
 * Institución a la que navegar desde una alerta, cuando el backend la permite identificar (FTR-0017 §7).
 * `SISDEU_INSTITUTION_CHANGED` la trae directa; `SISDEU_DUPLICATE_CONFLICT` la deriva del candidato
 * anclado (la propia cuenta de la alerta). Ninguna de las dos rutas inventa un id que el backend no dio.
 */
export const resolveAlertInstitutionId = (alert) => {
  const detail = alert?.detail || {};
  if (alert?.type === "SISDEU_INSTITUTION_CHANGED") {
    return detail.localInstitutionId != null ? Number(detail.localInstitutionId) : null;
  }
  if (alert?.type === "SISDEU_DUPLICATE_CONFLICT" && Array.isArray(detail.candidates)) {
    const anchored = detail.candidates.find(
      (candidate) => Number(candidate.localAccountId) === Number(alert.accountId)
    );
    return anchored?.linkedInstitutionId != null ? Number(anchored.linkedInstitutionId) : null;
  }
  return null;
};

export const formatSyncSummary = (summary = {}) => {
  const total = Number(summary.total) || 0;
  const created = Number(summary.created) || 0;
  const updated = Number(summary.updated) || 0;
  const blocked = Number(summary.blocked) || 0;
  const disabled = Number(summary.disabled) || 0;
  const plural = (count, word) => `${count} ${word}${count === 1 ? "" : "s"}`;
  return `${plural(total, "cuenta")}: ${plural(created, "creada")}, ${plural(updated, "actualizada")}, ${plural(
    blocked,
    "bloqueada"
  )}, ${plural(disabled, "desactivada")}.`;
};

/** Deduplicado defensivo por id: el backend ya no repite alertas abiertas, esto solo blinda la vista. */
export const dedupeAlertsById = (alerts = []) => {
  const seen = new Set();
  const result = [];
  for (const alert of alerts) {
    const id = alert?.id;
    if (id == null || seen.has(id)) continue;
    seen.add(id);
    result.push(alert);
  }
  return result;
};

export const countUnresolvedAlerts = (alerts = []) =>
  dedupeAlertsById(alerts).filter((alert) => !alert.resolvedAt).length;

/**
 * Impacto de un cambio de política, calculado sin endpoint de vista previa (no existe en el backend):
 * qué capacidades cambiarían y cuántas cuentas SISDEU de la compañía siguen HOY la política (las que
 * tienen una excepción individual no se ven afectadas). Ambos valores salen de datos ya cargados por el
 * propio admin (política vigente + listado de cuentas), nunca inventados.
 */
export const computePolicyChangeImpact = (currentPolicy = [], nextEnabledCapabilities = [], accounts = []) => {
  const nextEnabledSet = new Set(nextEnabledCapabilities);
  const changedCapabilities = currentPolicy
    .filter((row) => Boolean(row.enabled) !== nextEnabledSet.has(row.capability))
    .map((row) => row.capability);
  const policyFollowingSisdeuAccountCount = accounts.filter(
    (account) => account?.loginMethod === "SISDEU" && account?.capabilitiesMode === "POLICY"
  ).length;
  return { changedCapabilities, policyFollowingSisdeuAccountCount };
};
